import React, { useEffect, useState } from 'react';
import LocalizedLink from '../../LocalizedLink';
import { gql } from '@apollo/client';
import Loader from '../../Loader/Loader';
import Image, { ImageData } from '../../Image';
import { Sizes } from '../../../constants/Image';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client/react';

const GET_LATESTPOSTS = gql`
  query LatestPosts($ignoredPostSlug: String, $limit: Int) {
    latestPosts(ignoredPostSlug: $ignoredPostSlug, limit: $limit) {
      categories {
        id
        slug
        title
      }
      content
      cover {
        cdn_url
        urls {
          W800
        }
      }
      createdAt
      updatedAt
      id
      preview
      slug
      title
    }
  }
`;

type LatestPostsItem = {
  categories: {
    id: string;
    slug: string;
    title: string;
  }[];
  content: string;
  cover: ImageData;
  createdAt: string;
  updatedAt: string;
  id: string;
  preview: string;
  slug: string;
  title: string;
};

type Props = { ignoredPostSlug?: string; limit?: number };

export default function BlogBlock2({ ignoredPostSlug, limit }: Props) {
  const { loading, error, data } = useQuery<{
    latestPosts: LatestPostsItem[];
  }>(GET_LATESTPOSTS, {
    variables: { ignoredPostSlug, limit },
  });
  const [allPosts, setAllPosts] = useState<LatestPostsItem[]>([]);

  useEffect(() => {
    if (!loading && !error && data) {
      setAllPosts(data.latestPosts);
    }
  }, [loading, error, data]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex justify-center m-10">
        <Loader />
      </div>
    );
  }

  if (error) {
    return null;
  }

  return (
    <div className="my-20">
      {allPosts.length > 0 ? (
        <>
          <div className="flex flex-wrap lg:flex-nowrap justify-between items-center mb-2 gap-y-4">
            <>
              <h3 className="text-3xl md:text-4xl leading-10 font-axi-bold">
                Näpunäited mõnusamaks tervisespordiks
              </h3>
              <LocalizedLink
                className="flex items-center justify-center whitespace-nowrap font-axi-book text-sm rounded-4xl py-2.5 px-7 hover:shadow-2xl w-fit text-white bg-black hover:bg-rgrey-darker transition-all duration-100"
                to={'/blogi'}
                onClick={scrollToTop}
              >
                Mine blogisse
              </LocalizedLink>
            </>
          </div>
          <div className="relative pt-6 pb-8">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={24}
              slidesPerView={'auto'}
              navigation={{
                nextEl: '.blog-swiper-button-next',
                prevEl: '.blog-swiper-button-prev',
              }}
              loop={false}
              pagination={{
                el: '.blog-swiper-pagination',
                clickable: true,
              }}
            >
              {allPosts.map((post) => (
                <SwiperSlide key={post.id} className="w-80! h-auto shrink-0">
                  <LocalizedLink
                    to={`/blogi/${post.slug}`}
                    className="group flex flex-col h-full w-full"
                  >
                    <div className="shrink-0 relative block w-full h-54 overflow-hidden">
                      <Image
                        className="bg-rgrey-lightlight object-cover h-full w-full"
                        image={post.cover}
                        width="326"
                        height="220"
                        defaultSize={Sizes.W800}
                        alt={post.title}
                      />
                    </div>
                    <div className="flex flex-col pt-4 gap-y-1 grow">
                      <p className="text-sm text-black font-axi-book">
                        {dayjs(post.updatedAt).format('DD.MM.YYYY')}
                      </p>
                      <h3 className="text-black text-lg font-axi-bold">
                        {post.title}
                      </h3>
                      <span className="block text-rgreen text-sm font-axi-book opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-200 ease-in-out mt-auto">
                        Loe edasi
                        <ArrowLongRightIcon className="w-4 h-4 inline-block ml-1" />
                      </span>
                    </div>
                  </LocalizedLink>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="flex justify-start items-center gap-x-4 mt-12 -ml-1">
              <button
                aria-label="eelmine postitus"
                className="swiper-button-prev blog-swiper-button-prev"
              />

              <div className="swiper-pagination blog-swiper-pagination" />
              <button
                aria-label="järgmine postitus"
                className="swiper-button-next blog-swiper-button-next"
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
